<script>
  // @ts-check
  import { GlobalNavigation, Button } from "@ObamaFoundation/of-design-system";
  import { utilities } from "@ObamaFoundation/of-design-system";

  import { buildSrc, extractEntryContentType } from "$lib/utilities";
  import { heroThemeStore, heroFormFactorStore, mobileNavExpansionStore } from "$lib/stores";
  import { onMount, getContext } from "svelte";

  import SearchPanel from "./SearchPanel.svelte";
  import SearchIconButton from "./SearchIconButton.svelte";
  import { isExternalUrl } from "$lib/utilities/url-helpers";
  // eslint-disable-next-line  @ObamaFoundation/of-svelte/no-stores
  import { page } from "$app/stores";
  import { getImageAspectRatio } from "$lib/utilities/components/image-helpers";
  import { transformEventToDisplayFormat } from "$lib/utilities/eventFormatters";

  export let data;
  export let activePrimaryUrl;

  const menuItems = data.children?.filter((child) => extractEntryContentType(child) === "menu");
  const mainNavigation = [];
  let utilityNavigation = [];

  /** @type {{fullTextSearch?: boolean, querySuggestions?: boolean}} */
  const featureFlags = getContext("featureFlags") ?? {};

  $: currentPath = $page?.url.pathname;

  const pagesToHideDonateButton =
    import.meta.env.VITE_PAGES_TO_HIDE_DONATE?.split(",")?.filter(Boolean) || [];

  menuItems?.forEach((child) => {
    const link = {
      label: child?.fields?.trigger?.fields?.text ?? child?.trigger?.fields?.text,
      url:
        child?.fields?.trigger?.fields?.destination?.fields?.url ??
        child?.trigger?.fields?.destination?.fields?.url
    };

    const secondarySections = [];

    child?.fields?.items?.forEach((item) => {
      if (item?.fields?.type === "Static Menu") {
        secondarySections.push({
          type: "section",
          mobileStyle: "list",
          label: item?.fields?.trigger?.fields?.text,
          links: item?.fields?.items.map((link) => {
            return {
              label: link?.fields?.text,
              url: link?.fields?.url,
              isExternal: isExternalUrl(link?.fields.url)
            };
          }),
          trailingLink: item?.fields?.trailingItems?.length
            ? {
                label: item?.fields?.trailingItems[0]?.fields?.text,
                url: item?.fields?.trailingItems[0]?.fields?.url,
                isExternal: isExternalUrl(item?.fields?.trailingItems[0]?.fields?.url)
              }
            : undefined
        });
      } else if (item?.fields?.type === "Tertiary Menu") {
        secondarySections.push({
          type: "section",
          mobileStyle: "accordion",
          label: item?.fields?.trigger?.fields?.text,
          links: item?.fields?.items.map((link) => {
            return {
              label: link?.fields?.text,
              url: link?.fields?.url,
              isExternal: isExternalUrl(link?.fields.url)
            };
          }),
          trailingLink: item?.fields?.trailingItems?.length
            ? {
                label: item?.fields?.trailingItems[0]?.fields?.text,
                url: item?.fields?.trailingItems[0]?.fields?.url,
                isExternal: isExternalUrl(item?.fields?.trailingItems[0]?.fields?.url)
              }
            : undefined
        });
      } else if (item?.sys?.contentType?.sys?.id === "linkComponent") {
        secondarySections.push({
          type: "card",
          card: {
            title: item?.fields?.text,
            description: item?.fields?.destination?.fields?.description,
            linkLabel: item?.fields?.destination?.fields?.text,
            linkUrl: item?.fields?.destination?.fields?.url,
            isExternal: isExternalUrl(item?.fields?.destination?.fields.url),
            imageUrl: buildSrc(item?.fields?.image),
            imageAspectRatio: getImageAspectRatio(item?.fields?.image),
            alt: item?.fields?.image?.fields?.description
          }
        });
      } else if (item?.sys?.contentType?.sys?.id === "navTarget") {
        secondarySections.push({
          type: "card",
          card: {
            title: item?.fields?.text,
            description: item?.fields?.description,
            linkLabel: item?.fields?.textLinkDisplayText,
            linkUrl: item?.fields?.url,
            isExternal: isExternalUrl(item?.fields.url),
            imageUrl: buildSrc(item?.fields?.image),
            imageAspectRatio: getImageAspectRatio(item?.fields?.image),
            alt: item?.fields?.image?.fields?.description
          }
        });
      }
    });

    child?.fields?.externalItems?.forEach((item) => {
      const eventDisplayData = transformEventToDisplayFormat({ event: item.fields });

      let eventDescription = eventDisplayData.eventDate;
      if (eventDisplayData.eventTime) {
        eventDescription += ` • ${eventDisplayData.eventTime}`;
      }

      if (eventDisplayData.eventLocation) {
        eventDescription += `<br>${eventDisplayData.eventLocation}`;
      }
      secondarySections.push({
        type: "event",
        card: {
          title: eventDisplayData.eventTitle,
          description: eventDescription,
          linkLabel: eventDisplayData.cta.text,
          linkUrl: eventDisplayData.cta.url ?? "",
          isExternal: false,
          imageUrl: eventDisplayData.image?.src ?? null,
          alt: eventDisplayData.image?.alt ?? ""
        }
      });
    });

    mainNavigation.push({
      link,
      secondarySections
    });
  });

  onMount(() => {
    // Only animate the nav bar items after the first load
    animateNavBarItems = true;

    // short circuit
    if (pagesToHideDonateButton.includes(currentPath)) {
      return;
    }

    data.children
      ?.filter((child) => extractEntryContentType(child) === "linkComponent")
      .forEach((child) => {
        const isDonateButton = child.fields?.destination?.fields.url.includes("?form");
        const donateButtonOverrideUrl = $page?.data.pageEntry?.fields.donateButtonOverrideUrl ?? "";
        if (isDonateButton) {
          utilityNavigation.push({
            id: `primary-utility-nav-${child.sys.id}`,
            label: child.fields?.text,
            url: donateButtonOverrideUrl
              ? `${donateButtonOverrideUrl}`
              : child.fields?.destination.fields?.url
          });
        }
      });
    // eslint-disable-next-line no-self-assign
    utilityNavigation = utilityNavigation; // trigger component re-rendering
  });

  let isExpanded = false;
  let scrollYPosition;
  let delayThemeFadeOnNavBar = false;
  let animateNavBarItems = false;

  $: hasScrolledDown = !!scrollYPosition; // set to True when scrollYPosition is > 0, menaing user has scrolled down

  // Theme management
  let heroTheme;
  let isSearchPanelVisible = false;
  let isCtaAboveFoldHero = false;
  // Used to retrieve the current Home Hero background color to set the theme of the Global Navigation.
  heroThemeStore.subscribe((value) => {
    heroTheme = value;
  });

  // Used to determine if the hero component is of type "heroCtaAboveFold", then update the theme of the Global Navigation.
  heroFormFactorStore.subscribe((value) => {
    isCtaAboveFoldHero = value === "CTA Above Fold";
  });

  $: defaultNavTheme = hasScrolledDown || isCtaAboveFoldHero ? "white" : heroTheme;
  $: currentNavTheme = isSearchPanelVisible || isExpanded ? "lightGrey" : defaultNavTheme;
  $: theme = utilities.getTheme(currentNavTheme);

  $: if (isExpanded) {
    mobileNavExpansionStore.set(true);
  } else {
    mobileNavExpansionStore.set(false);
  }

  const handleUtilityLinkClick = (utilityLink) => {
    if (utilityLink.url.includes("?form") || utilityLink.label === "Donate") {
      isExpanded = false;
    }
  };

  $: {
    if (hasScrolledDown) {
      delayThemeFadeOnNavBar = false;
    } else {
      delayThemeFadeOnNavBar = true;

      // Allow animation to complete before resetting
      setTimeout(() => {
        delayThemeFadeOnNavBar = false;
      }, 500);
    }
  }

  $: transitionClass = animateNavBarItems ? "transition-all duration-[150ms] ease-nav-easing" : "";

  const displaySearchButton = featureFlags.fullTextSearch;
  const handleSearchOpen = () => {
    isSearchPanelVisible = true;
  };
  const handleSearchClose = () => {
    isSearchPanelVisible = false;
  };
</script>

<svelte:window bind:scrollY={scrollYPosition} />

<GlobalNavigation
  bind:theme={currentNavTheme}
  bind:isExpanded
  {hasScrolledDown}
  {mainNavigation}
  {delayThemeFadeOnNavBar}
  {animateNavBarItems}
  currentRoute={activePrimaryUrl}
  on:searchClose={handleSearchClose}
  {isSearchPanelVisible}
>
  <svelte:fragment slot="utilityNavigation">
    {#if displaySearchButton}
      <SearchIconButton
        focusOutlineColor={theme?.focusOutlineColor}
        hoverTextColor={theme?.hoverTextColor}
        on:click={handleSearchOpen}
      />
    {/if}
    {#each utilityNavigation as utilityLink, index}
      <Button
        id={utilityLink.id}
        bind:theme={currentNavTheme}
        data-transition-focus={`${utilityLink.label.replace(" ", "")}-${index}`}
        variant="filled"
        buttonClass="override-[nav]:px-8 override-[nav]:py-6 {transitionClass}"
        componentColorThemeOverride={currentNavTheme === "white" ? "cobaltBlue" : null}
        label={utilityLink.label}
        url={utilityLink.url}
      />
    {/each}
  </svelte:fragment>
  <svelte:fragment slot="utilityNavigationMobile">
    {#each utilityNavigation as utilityLink, index}
      <Button
        id="{utilityLink.id}-mobile"
        bind:theme={currentNavTheme}
        data-transition-focus={`${utilityLink.label.replace(" ", "")}-${index}`}
        variant="filled"
        buttonClass="override-[nav]:px-8 override-[nav]:py-6 {transitionClass}"
        componentColorThemeOverride={currentNavTheme === "white" ? "cobaltBlue" : null}
        label={utilityLink.label}
        url={utilityLink.url}
        on:click={() => handleUtilityLinkClick(utilityLink)}
      />
    {/each}
  </svelte:fragment>
  <svelte:fragment slot="searchButtonMobile">
    {#if displaySearchButton}
      <div class="mt-10">
        <SearchIconButton
          focusOutlineColor={theme?.focusOutlineColor}
          hoverTextColor={theme?.hoverTextColor}
          shouldIconInheritSize={true}
          on:click={handleSearchOpen}
        />
      </div>
    {/if}
  </svelte:fragment>
  <svelte:fragment slot="searchPanel">
    <SearchPanel bind:isSearchPanelVisible currentTheme={currentNavTheme} />
  </svelte:fragment>
</GlobalNavigation>
